import React, { useCallback, useMemo } from 'react'
import { Currency } from '@sequencehq/api'
import { GreyGrey10, GreyGrey30, Box, Flex } from '@sequencehq/core-components'
import { format } from '@sequencehq/utils/dates'
import {
  GroupHeaderCell,
  TotalHeaderCell,
  MonthHeaderCell,
  RecognizedHeaderCell,
  RemainingHeaderCell,
  GroupDataCell,
  TotalDataCell,
  MonthDataCell,
  RecognizedDataCell,
  RemainingDataCell,
  TotalSummaryCell,
  MonthSummaryCell,
  RecognizedSummaryCell,
  RemainingSummaryCell
} from '../RevenueWaterfallTableCells'
import type { RevenueWaterfallSortBy, WaterfallData } from '../types'
import { RevenueWaterfallSortState } from 'RevenueRecognition/view/revenueWaterfall/hooks/useRevenueWaterfallSorting'

const px = (value: number) => `${value}px`

const COLUMN_WIDTHS = {
  customer: 200,
  bookedTotal: 200,
  recognized: 125,
  remaining: 125
} as const

const SECTION_WIDTHS = {
  left: COLUMN_WIDTHS.customer + COLUMN_WIDTHS.bookedTotal,
  right: COLUMN_WIDTHS.recognized + COLUMN_WIDTHS.remaining
} as const

const MONTH_COLUMN = {
  minWidth: 120,
  width: '100%'
} as const

const getMonthlySectionMinWidth = (monthCount: number) =>
  monthCount * MONTH_COLUMN.minWidth

const getTableMinWidth = (monthCount: number) =>
  SECTION_WIDTHS.left +
  SECTION_WIDTHS.right +
  getMonthlySectionMinWidth(monthCount)

const STICKY_SECTION_Z_INDEX = 20

// Top header component
export const TopHeaderRow = ({ asOfDate }: { asOfDate: string }) => (
  <Flex
    position="sticky"
    top={0}
    zIndex={STICKY_SECTION_Z_INDEX + 3}
    bg="white"
    flexShrink={0}
  >
    <Box width={px(SECTION_WIDTHS.left)} flexShrink={0}>
      <Box
        height="32px"
        borderBottom={`1px solid ${GreyGrey30}`}
        borderRight={`1px solid ${GreyGrey30}`}
        bg={GreyGrey10}
        display="flex"
        alignItems="center"
        justifyContent="center"
        fontSize="12px"
        fontWeight="semibold"
        color="#4A5568"
      >
        Booked
      </Box>
    </Box>
    <Box flex={1}>
      <Box
        height="32px"
        borderBottom={`1px solid ${GreyGrey30}`}
        bg={GreyGrey10}
        display="flex"
        alignItems="center"
        justifyContent="center"
        fontSize="12px"
        fontWeight="semibold"
        color="#4A5568"
      >
        Recognized
      </Box>
    </Box>
    <Box width={px(SECTION_WIDTHS.right)} flexShrink={0}>
      <Box
        height="32px"
        borderBottom={`1px solid ${GreyGrey30}`}
        borderLeft={`1px solid ${GreyGrey30}`}
        bg={GreyGrey10}
        display="flex"
        alignItems="center"
        justifyContent="center"
        fontSize="12px"
        fontWeight="semibold"
        color="#4A5568"
      >
        As of {format(asOfDate, 'MMMM yyyy')}
      </Box>
    </Box>
  </Flex>
)

// Header row with sticky positioning
export const TableHeaderRow = ({
  data,
  sortState,
  onSort,
  primaryGroup,
  isFetching
}: {
  data: WaterfallData
  sortState: RevenueWaterfallSortState
  onSort: (column: RevenueWaterfallSortBy) => void
  primaryGroup?: string
  isFetching?: boolean
}) => (
  <Flex
    position="sticky"
    top="0px"
    zIndex={STICKY_SECTION_Z_INDEX + 2}
    bg="white"
    flexShrink={0}
    minWidth={px(getTableMinWidth(data.monthlyColumns.length))}
  >
    {/* Left frozen headers */}
    <Flex
      position="sticky"
      left={0}
      zIndex={STICKY_SECTION_Z_INDEX + 1}
      bg="white"
      width={px(SECTION_WIDTHS.left)}
      flexShrink={0}
    >
      <Box width={px(COLUMN_WIDTHS.customer)}>
        <GroupHeaderCell
          primaryGroup={primaryGroup}
          sortable
          active={sortState.sortBy === 'label'}
          sortDirection={sortState.sortDirection}
          onSort={() => onSort('label')}
          showSpinner={Boolean(isFetching && sortState.sortBy === 'label')}
        />
      </Box>
      <Box width={px(COLUMN_WIDTHS.bookedTotal)}>
        <TotalHeaderCell
          sortable
          active={sortState.sortBy === 'bookedTotal'}
          sortDirection={sortState.sortDirection}
          onSort={() => onSort('bookedTotal')}
          showSpinner={Boolean(
            isFetching && sortState.sortBy === 'bookedTotal'
          )}
        />
      </Box>
    </Flex>

    {/* Middle scrollable headers */}
    <Flex
      flex={1}
      minW={px(getMonthlySectionMinWidth(data.monthlyColumns.length))}
    >
      {data.monthlyColumns.map((month, index) => (
        <Box key={month} width={MONTH_COLUMN.width}>
          <MonthHeaderCell
            sortable
            month={month}
            isLast={index === data.monthlyColumns.length - 1}
            active={sortState.sortBy === month}
            sortDirection={sortState.sortDirection}
            onSort={() => onSort(month)}
            showSpinner={Boolean(isFetching && sortState.sortBy === month)}
          />
        </Box>
      ))}
    </Flex>

    {/* Right frozen headers */}
    <Flex
      position="sticky"
      right={0}
      zIndex={STICKY_SECTION_Z_INDEX + 1}
      bg="white"
      width={px(SECTION_WIDTHS.right)}
      flexShrink={0}
      borderLeft={`1px solid ${GreyGrey30}`}
    >
      <Box width={px(COLUMN_WIDTHS.recognized)}>
        <RecognizedHeaderCell
          sortable
          active={sortState.sortBy === 'recognized'}
          sortDirection={sortState.sortDirection}
          onSort={() => onSort('recognized')}
          showSpinner={Boolean(isFetching && sortState.sortBy === 'recognized')}
        />
      </Box>
      <Box width={px(COLUMN_WIDTHS.remaining)}>
        <RemainingHeaderCell
          sortable
          active={sortState.sortBy === 'remaining'}
          sortDirection={sortState.sortDirection}
          onSort={() => onSort('remaining')}
          showSpinner={Boolean(isFetching && sortState.sortBy === 'remaining')}
        />
      </Box>
    </Flex>
  </Flex>
)

// Inline style consts so they have stable references
const rowContainerStyle: React.CSSProperties = {
  position: 'absolute',
  top: 0,
  left: 0,
  width: '100%',
  willChange: 'transform'
}

const rowInnerStyle: React.CSSProperties = {
  display: 'flex',
  height: '100%'
}

const leftSectionStyle: React.CSSProperties = {
  position: 'sticky',
  left: 0,
  zIndex: STICKY_SECTION_Z_INDEX,
  background: 'white',
  width: px(SECTION_WIDTHS.left),
  flexShrink: 0,
  display: 'flex'
}

const rightSectionStyle: React.CSSProperties = {
  position: 'sticky',
  right: 0,
  zIndex: STICKY_SECTION_Z_INDEX,
  background: 'white',
  width: px(SECTION_WIDTHS.right),
  flexShrink: 0,
  borderLeft: `1px solid ${GreyGrey30}`,
  display: 'flex'
}

const columnStyle: React.CSSProperties = {
  width: MONTH_COLUMN.width
}

type VirtualDataRowProps = {
  row: any
  isExpanded: boolean
  virtualRow: { index: number; size: number; start: number }
  data: WaterfallData
  currency: Currency
  // primitives and not objects here means speedy comparison
  selectionMinRow: number
  selectionMaxRow: number
  selectionMinCol: number
  selectionMaxCol: number
  focusRow: number
  focusCol: number
  selectionVersion: number
  onCellClick: (row: number, col: number, e: React.MouseEvent) => void
  onCellMouseDown: (row: number, col: number, e: React.MouseEvent) => void
  onCellMouseEnter: (row: number, col: number) => void
  onCellRightClick: (
    row: number,
    col: number,
    e: React.MouseEvent,
    data: any,
    currency: string
  ) => void
}

export const VirtualDataRow = React.memo(
  ({
    row,
    isExpanded,
    virtualRow,
    data,
    currency,
    selectionMinRow,
    selectionMaxRow,
    selectionMinCol,
    selectionMaxCol,
    focusRow,
    focusCol,
    selectionVersion: _selectionVersion, // Unused in body but required for memo comparator
    onCellClick,
    onCellMouseDown,
    onCellMouseEnter,
    onCellRightClick
  }: VirtualDataRowProps) => {
    const rowIndex = virtualRow.index

    const isCellSelected = useCallback(
      (colIndex: number): boolean => {
        return (
          selectionMinRow >= 0 &&
          rowIndex >= selectionMinRow &&
          rowIndex <= selectionMaxRow &&
          colIndex >= selectionMinCol &&
          colIndex <= selectionMaxCol
        )
      },
      [
        rowIndex,
        selectionMinRow,
        selectionMaxRow,
        selectionMinCol,
        selectionMaxCol
      ]
    )

    const isCellFocused = useCallback(
      (colIndex: number): boolean => {
        return focusRow === rowIndex && focusCol === colIndex
      },
      [rowIndex, focusRow, focusCol]
    )

    const getSelectionBorders = useCallback(
      (colIndex: number) => {
        if (!isCellSelected(colIndex)) {
          return { top: false, right: false, bottom: false, left: false }
        }

        return {
          top: rowIndex === selectionMinRow,
          right: colIndex === selectionMaxCol,
          bottom: rowIndex === selectionMaxRow,
          left: colIndex === selectionMinCol
        }
      },
      [
        rowIndex,
        selectionMinRow,
        selectionMaxRow,
        selectionMinCol,
        selectionMaxCol,
        isCellSelected
      ]
    )

    const rowStyle = useMemo(
      () => ({
        ...rowContainerStyle,
        height: `${virtualRow.size}px`,
        transform: `translateY(${virtualRow.start}px)`
      }),
      [virtualRow.size, virtualRow.start]
    )

    const middleSectionStyle = useMemo(
      () => ({
        flex: 1,
        minWidth: px(getMonthlySectionMinWidth(data.monthlyColumns.length)),
        display: 'flex'
      }),
      [data.monthlyColumns.length]
    )

    // Store row.toggleExpanded in ref to avoid recreating callback on every row object change
    const toggleExpandedRef = React.useRef(row.toggleExpanded)
    toggleExpandedRef.current = row.toggleExpanded

    const toggleExpanded = useCallback(
      () => {
        toggleExpandedRef.current()
      },
      [row.id, rowIndex] // Stable callback - uses ref to access latest toggleExpanded function
    )

    // Memoize ALL cell callbacks to prevent GroupDataCell re-render
    const groupCellHandlers = useMemo(
      () => ({
        onMouseDown: (e: React.MouseEvent) => onCellMouseDown(rowIndex, 0, e),
        onMouseEnter: () => onCellMouseEnter(rowIndex, 0),
        onClick: (e: React.MouseEvent) => onCellClick(rowIndex, 0, e),
        onContextMenu: (e: React.MouseEvent) =>
          onCellRightClick(rowIndex, 0, e, data, currency)
      }),
      [
        rowIndex,
        onCellMouseDown,
        onCellMouseEnter,
        onCellClick,
        onCellRightClick,
        data,
        currency
      ]
    )

    return (
      <div
        style={rowStyle}
        data-virtual-index={rowIndex}
        data-testid="revrec.waterfall.table.row"
      >
        <div style={rowInnerStyle}>
          {/* Left frozen content - GROUP AND TOTAL */}
          <div style={leftSectionStyle}>
            {/* Group cell - Column 0 */}
            <div style={{ width: px(COLUMN_WIDTHS.customer) }}>
              <GroupDataCell
                rowOriginal={row.original}
                depth={row.depth}
                isExpanded={isExpanded}
                rowIndex={rowIndex}
                isFocused={isCellFocused(0)}
                isSelected={isCellSelected(0)}
                selectionBorders={getSelectionBorders(0)}
                onToggleExpanded={toggleExpanded}
                onMouseDown={groupCellHandlers.onMouseDown}
                onMouseEnter={groupCellHandlers.onMouseEnter}
                onClick={groupCellHandlers.onClick}
                onContextMenu={groupCellHandlers.onContextMenu}
              />
            </div>

            {/* Total cell - Column 1 */}
            <div style={{ width: px(COLUMN_WIDTHS.bookedTotal) }}>
              <TotalDataCell
                value={row.original.bookedTotal}
                currency={currency}
                rowIndex={rowIndex}
                isFocused={isCellFocused(1)}
                isSelected={isCellSelected(1)}
                selectionBorders={getSelectionBorders(1)}
                onMouseDown={e => onCellMouseDown(rowIndex, 1, e)}
                onMouseEnter={() => onCellMouseEnter(rowIndex, 1)}
                onClick={e => onCellClick(rowIndex, 1, e)}
                onContextMenu={e =>
                  onCellRightClick(rowIndex, 1, e, data, currency)
                }
              />
            </div>
          </div>

          {/* Middle scrollable content - MONTHLY COLUMNS */}
          <div style={middleSectionStyle}>
            {data.monthlyColumns.map((month, colIndex) => {
              const absoluteColIndex = colIndex + 2 // Offset by group + total columns
              return (
                <div key={month} style={columnStyle}>
                  <MonthDataCell
                    value={row.original.monthlyValues[month]}
                    currency={currency}
                    rowIndex={rowIndex}
                    isLast={colIndex === data.monthlyColumns.length - 1}
                    isFocused={isCellFocused(absoluteColIndex)}
                    isSelected={isCellSelected(absoluteColIndex)}
                    selectionBorders={getSelectionBorders(absoluteColIndex)}
                    onMouseDown={e =>
                      onCellMouseDown(rowIndex, absoluteColIndex, e)
                    }
                    onMouseEnter={() =>
                      onCellMouseEnter(rowIndex, absoluteColIndex)
                    }
                    onClick={e => onCellClick(rowIndex, absoluteColIndex, e)}
                    onContextMenu={e =>
                      onCellRightClick(
                        rowIndex,
                        absoluteColIndex,
                        e,
                        data,
                        currency
                      )
                    }
                  />
                </div>
              )
            })}
          </div>

          {/* Right frozen content - Recognized AND REMAINING */}
          <div style={rightSectionStyle}>
            {/* Recognized cell */}
            <div style={{ width: px(COLUMN_WIDTHS.recognized) }}>
              <RecognizedDataCell
                value={row.original.recognized}
                currency={currency}
                rowIndex={rowIndex}
                isFocused={isCellFocused(data.monthlyColumns.length + 2)}
                isSelected={isCellSelected(data.monthlyColumns.length + 2)}
                selectionBorders={getSelectionBorders(
                  data.monthlyColumns.length + 2
                )}
                onMouseDown={e =>
                  onCellMouseDown(rowIndex, data.monthlyColumns.length + 2, e)
                }
                onMouseEnter={() =>
                  onCellMouseEnter(rowIndex, data.monthlyColumns.length + 2)
                }
                onClick={e =>
                  onCellClick(rowIndex, data.monthlyColumns.length + 2, e)
                }
                onContextMenu={e =>
                  onCellRightClick(
                    rowIndex,
                    data.monthlyColumns.length + 2,
                    e,
                    data,
                    currency
                  )
                }
              />
            </div>

            {/* Remaining cell */}
            <div style={{ width: px(COLUMN_WIDTHS.remaining) }}>
              <RemainingDataCell
                value={row.original.remaining}
                currency={currency}
                rowIndex={rowIndex}
                isFocused={isCellFocused(data.monthlyColumns.length + 3)}
                isSelected={isCellSelected(data.monthlyColumns.length + 3)}
                selectionBorders={getSelectionBorders(
                  data.monthlyColumns.length + 3
                )}
                onMouseDown={e =>
                  onCellMouseDown(rowIndex, data.monthlyColumns.length + 3, e)
                }
                onMouseEnter={() =>
                  onCellMouseEnter(rowIndex, data.monthlyColumns.length + 3)
                }
                onClick={e =>
                  onCellClick(rowIndex, data.monthlyColumns.length + 3, e)
                }
                onContextMenu={e =>
                  onCellRightClick(
                    rowIndex,
                    data.monthlyColumns.length + 3,
                    e,
                    data,
                    currency
                  )
                }
              />
            </div>
          </div>
        </div>
      </div>
    )
  },
  (prev, next) => {
    // VirtualDataRow WILL re-render when position changes (that's fine and cheap)
    // The KEY is preventing CHILD components (GroupDataCell, etc.) from re-rendering
    // This is achieved by stabilizing all handler props

    const rowIndex = prev.virtualRow.index

    // Position or size changed → re-render (cheap, just updates transform)
    if (
      prev.virtualRow.start !== next.virtualRow.start ||
      prev.virtualRow.size !== next.virtualRow.size ||
      prev.virtualRow.index !== next.virtualRow.index
    ) {
      return false
    }

    // Row data changed → MUST re-render
    if (prev.row.id !== next.row.id) {
      return false
    }

    // Row payload changed (e.g. regroup/sort) → re-render
    if (prev.row.original !== next.row.original) {
      return false
    }

    // Check if expansion state changed → MUST re-render (CHECK THIS BEFORE FAST PATH!)
    const prevExpanded = prev.isExpanded
    const nextExpanded = next.isExpanded
    if (prevExpanded !== nextExpanded) {
      return false
    }

    // Fast path: selection version unchanged AND expansion unchanged → skip re-render
    if (prev.selectionVersion === next.selectionVersion) {
      return true
    }

    // Was this row selected before?
    const wasSelected =
      prev.selectionMinRow >= 0 &&
      rowIndex >= prev.selectionMinRow &&
      rowIndex <= prev.selectionMaxRow

    // Is this row selected now?
    const isSelected =
      next.selectionMinRow >= 0 &&
      rowIndex >= next.selectionMinRow &&
      rowIndex <= next.selectionMaxRow

    // If selection state changed for this row → re-render
    if (wasSelected !== isSelected) {
      return false
    }

    // If row is selected and boundaries changed → re-render
    if (
      isSelected &&
      (prev.selectionMinRow !== next.selectionMinRow ||
        prev.selectionMaxRow !== next.selectionMaxRow ||
        prev.selectionMinCol !== next.selectionMinCol ||
        prev.selectionMaxCol !== next.selectionMaxCol)
    ) {
      return false
    }

    // Check if focus moved to/from this row
    if (
      prev.focusRow !== next.focusRow &&
      (rowIndex === prev.focusRow || rowIndex === next.focusRow)
    ) {
      return false
    }

    // No relevant changes → skip re-render ✨
    return true
  }
)

export const TableSummaryRow = ({
  data,
  currency
}: {
  data: WaterfallData
  currency: Currency
}) => (
  <Flex
    position="sticky"
    bottom={0}
    zIndex={STICKY_SECTION_Z_INDEX + 2}
    bg="white"
    minWidth={px(getTableMinWidth(data.monthlyColumns.length))}
    data-testid="revrec.waterfall.table.totals"
  >
    {/* Left frozen summary */}
    <Flex
      position="sticky"
      left={0}
      zIndex={STICKY_SECTION_Z_INDEX + 1}
      bg="white"
      width={px(SECTION_WIDTHS.left)}
      flexShrink={0}
    >
      <Box width={px(SECTION_WIDTHS.left)}>
        <TotalSummaryCell value={data.totals.bookedTotal} currency={currency} />
      </Box>
    </Flex>

    {/* Middle scrollable summary */}
    <Flex
      flex={1}
      minW={px(getMonthlySectionMinWidth(data.monthlyColumns.length))}
    >
      {data.monthlyColumns.map((month, index) => (
        <Box key={month} width={MONTH_COLUMN.width}>
          <MonthSummaryCell
            value={data.totals.monthlyTotals[month]}
            currency={currency}
            isLast={index === data.monthlyColumns.length - 1}
          />
        </Box>
      ))}
    </Flex>

    {/* Right frozen summary */}
    <Flex
      position="sticky"
      right={0}
      zIndex={STICKY_SECTION_Z_INDEX + 1}
      bg="white"
      width={px(SECTION_WIDTHS.right)}
      flexShrink={0}
      borderLeft={`1px solid ${GreyGrey30}`}
    >
      <Box width={px(COLUMN_WIDTHS.recognized)}>
        <RecognizedSummaryCell
          value={data.totals.recognized}
          currency={currency}
        />
      </Box>
      <Box width={px(COLUMN_WIDTHS.remaining)}>
        <RemainingSummaryCell
          value={data.totals.remaining}
          currency={currency}
        />
      </Box>
    </Flex>
  </Flex>
)
